import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Download, Lock, FileText, Folder, FolderOpen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { GroupedDocuments, AggregatedClientDocument } from '../../types/aggregated-documents';
import { aggregatedDocumentsService } from '../../services/aggregated-documents-service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDbTimestamp } from '../../utils/datetime';
import { DocumentDescription } from './DocumentDescription';

interface GroupedDocumentsViewProps {
  groupedDocs: GroupedDocuments;
  selectedDocs: Set<string>;
  onToggleSelect: (docId: string) => void;
  onDownload: (doc: AggregatedClientDocument) => void;
  onPreview: (doc: AggregatedClientDocument) => void;
  /** Grava a finalidade do documento (null apaga); sem ela, só exibe. */
  getDescriptionSaver?: (doc: AggregatedClientDocument) => ((description: string | null) => Promise<void>) | undefined;
}

export const GroupedDocumentsView: React.FC<GroupedDocumentsViewProps> = ({
  groupedDocs,
  selectedDocs,
  onToggleSelect,
  onDownload,
  onPreview,
  getDescriptionSaver
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['CLIENT']));
  const [expandedCases, setExpandedCases] = useState<Set<string>>(new Set());

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleCase = (caseId: string) => {
    const newExpanded = new Set(expandedCases);
    if (newExpanded.has(caseId)) {
      newExpanded.delete(caseId);
    } else {
      newExpanded.add(caseId);
    }
    setExpandedCases(newExpanded);
  };

  const renderDocument = (doc: AggregatedClientDocument) => {
    const isSelected = selectedDocs.has(doc.id);
    const icon = aggregatedDocumentsService.getFileIcon(doc.mimeType);
    const size = aggregatedDocumentsService.formatFileSize(doc.fileSize);

    return (
      <div
        key={doc.id}
        className={`flex items-center justify-between gap-2 rounded-lg border p-3 hover:bg-muted ${
          isSelected ? 'border-primary bg-muted' : 'border-transparent'
        }`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(doc.id)}
            className="size-4 rounded-sm border-input accent-primary"
          />

          <span className="text-2xl" aria-hidden="true">{icon}</span>

          <div className="flex-1 min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <button
                onClick={() => onPreview(doc)}
                className="truncate text-sm font-medium hover:text-primary"
              >
                {doc.fileName}
              </button>
              {doc.isConfidential && (
                <Lock className="size-4 shrink-0 text-muted-foreground" aria-label="Confidencial" />
              )}
            </div>
            <DocumentDescription name={doc.fileName} description={doc.description} onSave={getDescriptionSaver?.(doc)} className="text-xs" />
            <div className="mt-1 truncate text-xs text-muted-foreground">
              {size} • {doc.uploadedByName} • {' '}
              {(() => {
                const uploadedAt = parseDbTimestamp(doc.uploadedAt);
                return uploadedAt ? format(uploadedAt, 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-';
              })()}
            </div>
          </div>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onDownload(doc)}
              className="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Baixar"
            >
              <Download className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Baixar</TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const renderGroup = (
    groupKey: string,
    title: string,
    count: number,
    documents: AggregatedClientDocument[],
    icon: string
  ) => {
    if (documents.length === 0) return null;

    const isExpanded = expandedGroups.has(groupKey);

    return (
      <div className="mb-4">
        <button
          onClick={() => toggleGroup(groupKey)}
          className="flex w-full items-center justify-between gap-2 rounded-lg border bg-muted/40 p-3 transition-colors hover:bg-muted sm:p-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            {isExpanded ? (
              <FolderOpen className="size-5 shrink-0 text-foreground" />
            ) : (
              <Folder className="size-5 shrink-0 text-muted-foreground" />
            )}
            <span className="text-2xl" aria-hidden="true">{icon}</span>
            <div className="min-w-0 text-left">
              <div className="truncate font-semibold">{title}</div>
              <div className="text-sm text-muted-foreground">{count} {count === 1 ? 'documento' : 'documentos'}</div>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="size-5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-2 space-y-1 sm:pl-4">
            {documents.map(renderDocument)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Documentos do Cliente */}
      {renderGroup(
        'CLIENT',
        'DOCUMENTOS DO CLIENTE',
        groupedDocs.CLIENT.length,
        groupedDocs.CLIENT,
        '📁'
      )}

      {/* Documentos de Casos */}
      {groupedDocs.CASE.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => toggleGroup('CASE')}
            className="flex w-full items-center justify-between gap-2 rounded-lg border bg-muted/40 p-3 transition-colors hover:bg-muted sm:p-4"
          >
            <div className="flex min-w-0 items-center gap-3">
              {expandedGroups.has('CASE') ? (
                <FolderOpen className="size-5 shrink-0 text-foreground" />
              ) : (
                <Folder className="size-5 shrink-0 text-muted-foreground" />
              )}
              <span className="text-2xl" aria-hidden="true">⚖️</span>
              <div className="min-w-0 text-left">
                <div className="truncate font-semibold">DOCUMENTOS DE CASOS</div>
                <div className="text-sm text-muted-foreground">
                  {groupedDocs.CASE.reduce((sum, c) => sum + c.documents.length, 0)} documentos em {groupedDocs.CASE.length} {groupedDocs.CASE.length === 1 ? 'caso' : 'casos'}
                </div>
              </div>
            </div>
            {expandedGroups.has('CASE') ? (
              <ChevronDown className="size-5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
            )}
          </button>

          {expandedGroups.has('CASE') && (
            <div className="mt-2 space-y-3 sm:pl-4">
              {groupedDocs.CASE.map((caseGroup) => {
                const isCaseExpanded = expandedCases.has(caseGroup.caseId);

                return (
                  <div key={caseGroup.caseId} className="border-l-2 border-border pl-4">
                    <button
                      onClick={() => toggleCase(caseGroup.caseId)}
                      className="flex w-full items-center justify-between rounded-lg border bg-card p-3 hover:bg-muted"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {isCaseExpanded ? (
                          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                        )}
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 text-left">
                          <div className="break-words text-sm font-medium">
                            {caseGroup.caseTitle}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {caseGroup.documents.length} {caseGroup.documents.length === 1 ? 'documento' : 'documentos'}
                          </div>
                        </div>
                      </div>
                    </button>

                    {isCaseExpanded && (
                      <div className="mt-2 space-y-1">
                        {caseGroup.documents.map(renderDocument)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Documentos de Tarefas */}
      {renderGroup(
        'TASK',
        'DOCUMENTOS DE TAREFAS',
        groupedDocs.TASK.length,
        groupedDocs.TASK,
        '📋'
      )}

      {/* Documentos de Atendimentos */}
      {renderGroup(
        'INTERACTION',
        'DOCUMENTOS DE ATENDIMENTOS',
        groupedDocs.INTERACTION.length,
        groupedDocs.INTERACTION,
        '💬'
      )}

      {/* Documentos Financeiros */}
      {renderGroup(
        'FINANCE',
        'DOCUMENTOS FINANCEIROS',
        groupedDocs.FINANCE.length,
        groupedDocs.FINANCE,
        '💰'
      )}
    </div>
  );
};
